import { AfterViewInit, Component, OnInit, ViewChild } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { ActivatedRoute, Params, Router, RouterPreloader } from '@angular/router';

import { LoginFormStep } from './login.enum';

import { ApplicationId, AuthenticationState, ProviderType, strictEmailValidator } from '../../../constants';
import { IAccountDetails, IApplicationProfile, ILoginCredentials, ISecurityEvent, SecurityEvents } from '../../../interfaces/security.interfaces';
import { ErrorReportingService } from '../../../services/error-reporting.service';
import { FullPageMaskService } from '../../../services/full-page-mask.service';
import { SecurityService } from '../../../services/security.service';
import { AppConfigService } from '../../../services/app-config.service';
import { LoginBackgroundService } from '../../../services/login-background.service';

// Material
import { MatPaginator } from '@angular/material/paginator';
import { MatSort } from '@angular/material/sort';
import { MatTableDataSource } from '@angular/material/table';
import { ILogin, ILoginAccount, ILoginAuthenticateCode, ILoginBasic, ILoginContact, ILoginSuperUser } from '../../../interfaces/login.interface';
import { AccountContact } from '../../../models/account-contact.model';
import { AccountContactDTO } from '../../../models/dtos/account-contact.dto';

@Component({
  selector: 'com-login',
  templateUrl: './login.component.html',
  styleUrls: ['./login.component.scss'],
})
export class LoginComponent implements OnInit, AfterViewInit {
  @ViewChild(MatSort) sort: MatSort;
  @ViewChild(MatPaginator) paginator: MatPaginator;
  dataSource = new MatTableDataSource<IAccountDetails>(null);
  displayedColumns = ['accountCode', 'accountName', 'providerType', 'actions'];

  isAuthenticated: boolean;
  returnUrl: string;
  redirectFromELicensing: string;
  badLogin = false;
  requestAuthCodeFailed = false;
  accountCodeIsValid = true;
  contactButtonText: string;
  secondCount = 5;
  loginFormGroup: FormGroup<ILogin>;
  loginFormStep = LoginFormStep;
  step: LoginFormStep = LoginFormStep.stepBasicLogin;
  contacts: Array<AccountContact>;
  accounts: Array<IAccountDetails>;
  accountsLoading: boolean = false;

  cancelLoginButtonText: string;

  private selectedContact: AccountContactDTO;

  constructor(
    private security: SecurityService,
    private router: Router,
    private route: ActivatedRoute,
    private maskService: FullPageMaskService,
    private errorReporting: ErrorReportingService,
    private fb: FormBuilder,
    private loginBackground: LoginBackgroundService,
    private env: AppConfigService
  ) {
    security.userEvents.subscribe((event: ISecurityEvent) => {
      if (event?.eventType === SecurityEvents.badCredentials) {
        this.badLogin = true;
      }
    });

    this.cancelLoginButtonText = route.snapshot.queryParamMap.get('reason') === 'switch' ? 'Log Out' : 'Cancel Log In';
  }

  get applicationName(): string {
    return this.security.applicationName;
  }

  get applicationProfile(): IApplicationProfile {
    return this.security.applicationProfile;
  }

  get isSuperUser(): boolean {
    return this.security.isSuperUser;
  }

  get contactName() {
    return this.security.profile.user.name;
  }
  get contactEmail() {
    return this.security.profile.user.email;
  }
  get stepBasicLogin() {
    return this.loginFormGroup?.controls.basic;
  }

  get stepContact() {
    return this.loginFormGroup?.controls.contact;
  }
  get stepAccount() {
    return this.loginFormGroup?.controls.account;
  }
  get stepAuthenticateCode() {
    return this.loginFormGroup?.controls.authenticateCode;
  }
  get stepSuperUser() {
    return this.loginFormGroup?.controls.superUser;
  }
  get emailAddress() {
    return this.stepBasicLogin?.controls.emailAddress;
  }
  get password() {
    return this.stepBasicLogin?.controls.password;
  }
  get rememberMe() {
    return this.stepBasicLogin?.controls.rememberMe;
  }
  get contact() {
    return this.stepContact?.controls.contact;
  }
  get authCode() {
    return this.stepAuthenticateCode?.controls.authCode;
  }
  get accountCode() {
    return this.stepSuperUser?.controls.accountCode;
  }

  get stepText() {
    var text: string = 'Log In';
    switch (this.step) {
      case LoginFormStep.stepMfa:
        text = 'Authenticator Code';
        break;
      case LoginFormStep.stepSuperUser:
        text = 'Administrator Mode';
        break;
      case LoginFormStep.stepContact:
        text = 'Select Contact';
        break;
      case LoginFormStep.stepAccount:
        text = 'Select Account';
        break;
      case LoginFormStep.stepNoAccounts:
        text = 'No Associated Accounts';
        break;
    }
    return text;
  }

  get subtitleText() {
    return this.applicationProfile.showSignUpButton ? 'Please log in or sign up below' : 'Please log in here';
  }

  async ngOnInit() {
    //If the user has stored a bookmark for the login page then it will more than likely have an invalid 'state' value
    //stored in the return url. This value is subsequently used to retreive OIDC related information from the local
    //storage and if it can't be found the login attempt will fail.
    //So we need to parse the returnUrl, and check if the state value has a corresponding entry in the local storage
    //and if not then reload the page from the root as this will regenerate that information.
    this.route.queryParams.subscribe((routeParams: Params) => {
      if (routeParams?.ReturnUrl) {
        var returnUrl: URL = new URL(routeParams?.ReturnUrl);
        var state: string = returnUrl.searchParams.get('state');
        if (state) {
          if (!localStorage.getItem(`oidc.${state}`)) {
            this.router.navigate(['/'], { replaceUrl: true });
          }
        }
      }
    });

    this.loginBackground.showLoginBackground = true;
    this.loginFormGroup = this.createFormGroup();
    const user = this.security.user;
    if (this.security.accountIsVerified) {
      this.isAuthenticated = true;
    } else if (user != null) {
      this.dataSource = new MatTableDataSource<IAccountDetails>(null);

      if (this.security.authenticationState == AuthenticationState.mfaRequired && this.security.profile.user?.mfaDisabled) {
        //User has MFA disabled for some reason (should not be the norm) so just "pretend" it has been completed
        this.mfaCompleted(null);
      } else {
        this.goToNextStep(this.getNextStep());
      }
    } else {
      this.returnUrl = this.route.snapshot.queryParamMap.get('ReturnUrl');

      if (this.returnUrl == null) {
        this.security.startAuthentication();
      }
    }
  }

  ngAfterViewInit(): void {
    if (this.step == this.loginFormStep.stepAccount) {
      this.paginator.pageSize = 5;
      this.dataSource.paginator = this.paginator;
      this.dataSource.sort = this.sort;
    }
  }

  async login() {
    if (this.loginFormGroup.controls.basic.valid) {
      this.badLogin = false;

      var credentials: ILoginCredentials;

      credentials = {
        username: this.emailAddress.value,
        password: this.password.value,
        rememberLogin: this.rememberMe.value,
        returnUrl: this.returnUrl,
      };

      this.maskService.showLoading();
      await this.security.login(credentials);
      this.maskService.hideLoading();
    }
  }

  logout() {
    this.security.logout();
  }

  home() {
    this.router.navigateByUrl('/');
  }

  goToNextStep(step: LoginFormStep) {
    this.step = step;
  }

  getContacts() {
    this.maskService.showLoading();
    this.security.getContacts(this.isSuperUser, false).then((res: AccountContact[]) => {
      this.contacts = res;
      this.maskService.hideLoading();
    });
  }

  confirmContact() {
    if (this.isSuperUser && this.loginFormGroup.controls.contact.valid) {
      this.selectedContact = this.stepContact.controls.contact.value;
      this.security.profile.user.name = `${this.selectedContact.firstName} ${this.selectedContact.lastName}`;
      this.security.profile.user.email = this.selectedContact.email;

      this.security.profile.selectedAccount.contactId = this.selectedContact.lcAuto;
      this.security.profile.selectedAccount.providerTypeString = ProviderType[this.security.profile.selectedAccount.providerType];
      this.security.profile.selectedAccount.roleId = this.contacts.filter((x) => x.lcAuto === this.selectedContact.lcAuto)[0]?.roleId;
      this.security.profile.selectedAccount.accountPermissions = this.selectedContact.accountPermissions;

      this.router.navigateByUrl(this.security.redirectToHome());

      this.security.markAccountAsVerified();
    }
  }

  async validateEnteredAccountCode() {
    if (this.loginFormGroup.controls.superUser.valid) {
      this.validateAccountCode(
        this.isSuperUser
          ? this.loginFormGroup.controls.superUser.controls.accountCode.value
          : this.loginFormGroup.controls.account.controls.accountCode.value
      );
    }
  }

  async validateAccountCode(accountCode: string) {
    this.accountCodeIsValid = true;
    try {
      this.maskService.showLoading();
      this.accountCodeIsValid = await this.security.validateAccountCode(accountCode);
      this.maskService.hideLoading();
      if (this.accountCodeIsValid) {
        if (this.isSuperUser) {
          this.getContacts();
          this.goToNextStep(LoginFormStep.stepContact);
          this.contactButtonText = 'Confirm Login';
        } else {
          this.router.navigateByUrl(this.security.redirectToHome());
          this.security.markAccountAsVerified();
        }
      }
    } catch (error) {
      this.errorReporting.logError('Account code validation failed', 'login.validateAccountCode');
      this.accountCodeIsValid = false;
      this.maskService.hideLoading();
    }
  }

  private createFormGroup(): FormGroup<ILogin> {
    return this.fb.group<ILogin>({
      basic: this.fb.group<ILoginBasic>({
        emailAddress: this.fb.control<string>('', [Validators.required, strictEmailValidator]),
        password: this.fb.control<string>('', [Validators.required]),
        rememberMe: this.fb.control<boolean>(false, []),
      }),
      contact: this.fb.group<ILoginContact>({
        contact: this.fb.control<AccountContactDTO>(null, [Validators.required]),
      }),
      superUser: this.fb.group<ILoginSuperUser>({
        accountCode: this.fb.control<string>('', [Validators.required, Validators.pattern(new RegExp('^[0-9]{6}$|^cash01$', 'i'))]), //cash01 is a special case legacy finance account we don't support any other non-numeric account codes
      }),
      account: this.fb.group<ILoginAccount>({
        accountCode: this.fb.control<string>('', [Validators.required]),
      }),
      authenticateCode: this.fb.group<ILoginAuthenticateCode>({
        authCode: this.fb.control<string>('', [Validators.required]),
      }),
    });
  }

  accountSelect(account: IAccountDetails) {
    if (this.validateAccountCode(account.accountCode)) {
      this.security.profile.selectedAccount = account;
      this.security.markAccountAsVerified();
      this.router.navigateByUrl(this.security.redirectToHome());
    }
  }

  applyFilter(event: Event) {
    const filterValue = (event.target as HTMLInputElement).value;
    this.dataSource.filter = filterValue.trim().toLowerCase();
  }

  mfaCompleted(event: Event) {
    this.security.mfaCompleted();
    this.goToNextStep(this.getNextStep());
  }

  getNextStep(): LoginFormStep {
    let nextStep: LoginFormStep;
    if (this.security.authenticationState === AuthenticationState.authCodeRequested) {
      nextStep = LoginFormStep.stepAuthenticateCode;
    } else if (this.security.authenticationState === AuthenticationState.selectAccountCode) {
      nextStep = LoginFormStep.stepSuperUser;
    } else if (this.security.authenticationState === AuthenticationState.selectAccount) {
      if (this.security.profile?.user?.accounts.length === 1) {
        this.accountSelect(this.security.profile.user.accounts[0]);
      } else {
        this.accounts = this.security.profile.user.accounts;
        this.dataSource = new MatTableDataSource<IAccountDetails>(this.accounts);
        setTimeout(() => {
          this.dataSource.paginator = this.paginator;
          this.dataSource.sort = this.sort;
        }, 0);
        nextStep = LoginFormStep.stepAccount;
      }
    } else if (this.security.authenticationState === AuthenticationState.noAccounts) {
      nextStep = LoginFormStep.stepNoAccounts;
    } else if (this.security.authenticationState === AuthenticationState.mfaRequired) {
      nextStep = LoginFormStep.stepMfa;
    } else {
      this.getContacts();
      nextStep = LoginFormStep.stepContact;
      this.contactButtonText = 'Request Authentication Code';
    }
    return nextStep;
  }

  signUp() {
    this.router.navigateByUrl('sign-up');
  }

  applyForAccount() {
    this.loginBackground.showLoginBackground = false;
    this.router.navigateByUrl('accounts/registration');
  }

  showDataPortalGuidance(): boolean {
    return this.env.config.applicationId !== ApplicationId.DataPortal;
  }

  showeLicensingGuidance(): boolean {
    return this.env.config.applicationId !== ApplicationId.eLicensing;
  }

  showSenderIdGuidance(): boolean {
    return this.env.config.applicationId !== ApplicationId.SenderId;
  }
}
